import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring-offset-background",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        warning:
          "bg-amber-100 text-amber-800 border border-amber-200 shadow-sm hover:bg-amber-200/80 dark:bg-amber-400/20 dark:text-amber-100 dark:border-amber-500/40",
        soft: "bg-orange-100 text-orange-700 border border-orange-200 shadow-sm hover:bg-orange-200/80 dark:bg-orange-400/25 dark:text-orange-50 dark:border-orange-500/40",
        subtle:
          "bg-slate-100 text-slate-700 border border-slate-200 shadow-sm hover:bg-slate-200/80 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        pastel:
          "bg-gradient-to-r from-rose-200 via-orange-200 to-amber-200 text-rose-900 shadow-sm hover:from-rose-300 hover:via-orange-300 hover:to-amber-300 dark:from-rose-400/80 dark:via-orange-400/80 dark:to-amber-400/80 dark:text-rose-50",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
